import React, {
    createContext,
    Dispatch,
    ReactNode,
    SetStateAction,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react';

import { App } from 'antd';

import { handleServiceError } from 'lib/helpers/ServiceHelper';
import { listServiceCategory } from 'services/ServiceCategory.service';
import { listSuppliers } from 'services/Supplier.service';

type Value = {
    isLoading: boolean,
    suppliers: Supplier.Model[],
    suppliersFiltered: Supplier.Model[],
    serviceCategories: Supplier.ServiceCategory[],
    supplier: Supplier.Model | null,
    supplierId: Supplier.Model['id'] | null,
    setSupplierId: Dispatch<SetStateAction<Value['supplierId']>>,
    isFormVisible: boolean,
    setIsFormVisible: Dispatch<SetStateAction<Value['isFormVisible']>>,
    setSuppliersFiltered: Dispatch<SetStateAction<Value['suppliersFiltered']>>,
    fetchSuppliers: () => Promise<void>,
    supplierNames: string[],
    supplierCategories: string[]
    emailOptions: string[],
    responsibleOptions: string[],
    cellphoneOptions: string[],
    phoneOptions: string[]
};

type Props = {
    children: (value: Value) => ReactNode
};

const SupplierContext = createContext<Value | null>(null);

export function SupplierContextProvider({ children }: Props) {
    const app = App.useApp();

    const [isLoading, setIsLoading] = useState(false);

    const [suppliers, setSuppliers] = useState<Supplier.Model[]>([]);
    const [suppliersFiltered, setSuppliersFiltered] = useState<Supplier.Model[]>([]);
    const [serviceCategories, setServiceCategories] = useState<Supplier.ServiceCategory[]>([]);

    const [supplierId, setSupplierId] = useState<Supplier.Model['id'] | null>(null);
    const [isFormVisible, setIsFormVisible] = useState(false);

    const fetchSuppliers = async () => {
        setIsLoading(true);

        const response = await listSuppliers();

        if (!response.success) {
            setIsLoading(false);
            return handleServiceError(app, response);
        }

        setSuppliers(response.suppliers);
        setSuppliersFiltered(response.suppliers);
        setIsLoading(false);
    };

    const fetchServiceCategories = async () => {
        const response = await listServiceCategory();
        if (!('status' in response)) {
            setServiceCategories(response);
        }
    };

    const memoizedFetchSuppliers = useCallback(fetchSuppliers, [app]);

    useEffect(() => {
        memoizedFetchSuppliers();
        fetchServiceCategories();
    }, [memoizedFetchSuppliers]);

    const supplier = useMemo(() => {
        if (!supplierId || suppliers.length === 0) return null;
        return suppliers.find(s => s.id === supplierId) || null;
    }, [suppliers, supplierId]);

    const supplierNames = useMemo(() => {
        const names = suppliers.map(s => s.name).filter(Boolean);
        return Array.from(new Set(names)).sort();
    }, [suppliers]);

    const supplierCategories = useMemo(() => {
        return serviceCategories.map(sc => sc.name).filter(Boolean).sort();
    }, [serviceCategories]);

    const emailOptions = useMemo(() => {
        return Array.from(new Set(suppliers.map(s => s.email).filter(Boolean))).sort();
    }, [suppliers]);

    const responsibleOptions = useMemo(() => {
        return Array.from(new Set(suppliers.map(s => s.accountable).filter(Boolean))).sort();
    }, [suppliers]);

    const cellphoneOptions = useMemo(() => {
        return Array.from(new Set(suppliers.map(s => s.phone_1).filter(Boolean))).sort();
    }, [suppliers]);

    const phoneOptions = useMemo(() => {
        return Array.from(new Set(suppliers.map(s => s.phone_2).filter(Boolean))).sort();
    }, [suppliers]);


    const value: Value = {
        isLoading,
        suppliers,
        suppliersFiltered,
        serviceCategories,
        supplier,
        supplierId,
        setSupplierId,
        isFormVisible,
        setIsFormVisible,
        setSuppliersFiltered,
        fetchSuppliers: memoizedFetchSuppliers,
        supplierNames,
        supplierCategories,
        emailOptions,
        responsibleOptions,
        cellphoneOptions,
        phoneOptions
    };

    return (
        <SupplierContext.Provider value={value}>
            {children(value)}
        </SupplierContext.Provider>
    );
}

export function useSupplier() {
    const context = useContext(SupplierContext);
    if (!context)
        throw new Error('useSupplier must be used within a SupplierContextProvider');
    return context;
}
